import {testIdProps} from '@github-ui/test-id-props'
import {type ResponsiveValue, useResponsiveValue} from '@primer/react'
import type {OcticonProps} from '@primer/react/deprecated'
// eslint-disable-next-line primer-react/use-styled-react-import
import {Octicon} from '@primer/styled-react/deprecated'
import {clsx} from 'clsx'
import {type PropsWithChildren, type ReactNode, useEffect} from 'react'

import {useListViewVariant} from '../ListView/VariantContext'
import {useListItemAlignment} from './AlignmentContext'
import styles from './LeadingVisual.module.css'
import {useListItemNewActivity} from './NewActivityContext'
import {useListItemStatus} from './StatusContext'
import {ListItemUnreadIndicator} from './UnreadIndicator'

const DEFAULT_VISUAL_SIZE = 16

export type ListItemLeadingVisualProps = {
  /**
   * The Octicon component representing the desired icon
   */
  icon?: OcticonProps['icon']
  /**
   * Render any child content as the leading visual
   * NOTE: if both icon and children are passed, the icon will be rendered first, with the children appended after
   */
  children?: ReactNode
  /**
   * Additional className prop to override or extend pre-existing className styles
   */
  className?: string
  /**
   * The name of the Primer variable that will be used for the icon's fill
   * eg.: done.fg
   */
  color?: string
  /**
   * A text description of the item
   * Used for visually hidden text to describe the item on focus for the screen readers
   */
  description?: string
  /**
   * Indicates whether the rendered item has new content since lastViewed
   */
  newActivity?: boolean
  /**
   * Size of the leading visual in pixels (width and height). Default is 16px.
   */
  size?: number | ResponsiveValue<number>
}

export function ListItemLeadingVisual({
  icon,
  color,
  description,
  newActivity = false,
  className,
  children,
  size: sizeProp = DEFAULT_VISUAL_SIZE,
  ...props
}: ListItemLeadingVisualProps & PropsWithChildren) {
  const {variant} = useListViewVariant()
  const {setStatus} = useListItemStatus()
  const {setHasNewActivity} = useListItemNewActivity()
  const trimmedDescription = description?.trim()
  const {alignment} = useListItemAlignment()
  const size = useResponsiveValue(sizeProp, DEFAULT_VISUAL_SIZE)

  useEffect(() => {
    setHasNewActivity(newActivity)
  }, [newActivity, setHasNewActivity])

  useEffect(() => {
    if (trimmedDescription) setStatus(trimmedDescription)
  }, [setStatus, trimmedDescription])

  // We use margin-top to center the leading visual with the top row of the title because the
  // title can wrap onto multiple lines
  const marginTop = alignment === 'center' ? 0 : (variant === 'compact' ? 18 : newActivity ? 13 : 22) - size / 2

  return (
    <div className={clsx(styles.outer, className)} style={{marginTop: `${marginTop}px`}} {...props}>
      <div>
        <div
          {...testIdProps('list-view-leading-visual')}
          className={styles.inner}
          style={{width: `${size}px`, height: `${size}px`}}
        >
          {icon && <Octicon aria-label="" icon={icon} color={color} />}
          {children}
          {trimmedDescription && (
            <span className="sr-only" {...testIdProps('leading-visual-text-description')}>
              {trimmedDescription}
            </span>
          )}
        </div>
      </div>
      {newActivity && <ListItemUnreadIndicator />}
    </div>
  )
}

try{ ListItemLeadingVisual.displayName ||= 'ListItemLeadingVisual' } catch {}